import { msal, API_SCOPE } from "../auth/msal";

const API_BASE = "https://liaise-pricing-api.azurewebsites.net/api";

async function getToken(): Promise<string> {
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) throw new Error("No account");
  msal.setActiveAccount(account);
  const result = await msal
    .acquireTokenSilent({ scopes: [API_SCOPE], account })
    .catch(() => msal.acquireTokenPopup({ scopes: [API_SCOPE], account }));
  return result.accessToken;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string>),
  };
  const r = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`${r.status} ${r.statusText}: ${text}`);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export const api = {
  me:           () => call<any>("/me"),
  homes:        () => call<any>("/pricing/homes"),
  funders:      () => call<any>("/pricing/funders"),
  peopleByHome: (homeId?: number) => call<any>(`/pricing/people-supported${homeId ? `?home_id=${homeId}` : ""}`),
  rateCard:     () => call<any>("/pricing/rate-card"),
  myEstimates:  (status?: string) => call<any>(`/pricing/estimates${status ? `?status=${status}` : ""}`),
  createEstimate: (body: any) => call<any>("/pricing/estimates", { method: "POST", body: JSON.stringify(body) }),
  getEstimate:  (id: string) => call<any>(`/pricing/estimates/${id}`),
  updateEstimate: (id: string, body: any) => call<any>(`/pricing/estimates/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteEstimate: (id: string) => call<any>(`/pricing/estimates/${id}`, { method: "DELETE" }),
  replaceDays:  (id: string, days: any[]) => call<any>(`/pricing/estimates/${id}/days`, { method: "POST", body: JSON.stringify({ days }) }),
  estimateAction: (id: string, action: string, body: any = {}) =>
    call<any>(`/pricing/estimates/${id}/actions/${action}`, { method: "POST", body: JSON.stringify(body) }),
  estimatesBulk: (action: "submit" | "delete", ids: string[], reason?: string) =>
    call<any>(`/pricing/estimates/bulk/${action}`, { method: "POST", body: JSON.stringify({ ids, reason }) }),
  approvalsList: (stage = "queue") => call<any>(`/approvals?stage=${stage}`),
  approvalAction: (id: string, action: string, body: any = {}) =>
    call<any>(`/approvals/${id}/actions/${action}`, { method: "POST", body: JSON.stringify(body) }),
  adjustmentCreate: (id: string, amount_pence: number, reason: string) =>
    call<any>(`/approvals/${id}/adjustments`, { method: "POST", body: JSON.stringify({ amount_pence, reason }) }),
  adjustmentUpdate: (id: string, adjId: string, amount_pence: number, reason: string) =>
    call<any>(`/approvals/${id}/adjustments/${adjId}`, { method: "PUT", body: JSON.stringify({ amount_pence, reason }) }),
  adjustmentDelete: (id: string, adjId: string) =>
    call<any>(`/approvals/${id}/adjustments/${adjId}`, { method: "DELETE" }),
  allEstimates: (params: Record<string, string> = {}) => {
    const qs = new URLSearchParams(params).toString();
    return call<any>(`/manage/all-estimates${qs ? `?${qs}` : ""}`);
  },
  policy:       () => call<any>("/manage/policy"),
  policyUpdate: (key: string, value: string) =>
    call<any>(`/manage/policy/${key}`, { method: "PUT", body: JSON.stringify({ value }) }),
  claimantProfile: (oid: string) => call<any>(`/manage/claimants/${oid}`),
  report: (kind: string, params: Record<string, string> = {}) => {
    const qs = new URLSearchParams(params).toString();
    return call<any>(`/manage/reports/${kind}${qs ? `?${qs}` : ""}`);
  },
  rateCardAdmin: () => call<any>("/manage/rate-card"),
  rateCardAdd:   (body: { key: string; pence: number; effective_from: string; notes?: string }) =>
    call<any>("/manage/rate-card", { method: "POST", body: JSON.stringify(body) }),
  approvers:     () => call<any>("/manage/approvers"),
  approverUpdate:(id: number, body: any) =>
    call<any>(`/manage/approvers/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  meOoo:         (ooo_from: string | null, ooo_to: string | null) =>
    call<any>("/me/ooo", { method: "PUT", body: JSON.stringify({ ooo_from, ooo_to }) }),
  pwsProfile:    (id: number) => call<any>(`/manage/pws/${id}`),
  homeProfile:   (id: number) => call<any>(`/manage/homes/${id}`),
  funderProfile: (id: number) => call<any>(`/manage/funders/${id}`),
  funderUpdate:  (id: number, body: { contact_email?: string | null }) =>
    call<any>(`/manage/funders/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  // sendToFunder removed 2026-05-20 — endpoint returns 410 by design.
  pwsSyncStatus: () => call<any>("/manage/pws-sync"),
  pwsSyncRun:    () => call<any>("/manage/pws-sync", { method: "POST" }),
  commentsList:  (estimateId: string) => call<any>(`/pricing/estimates/${estimateId}/comments`),
  commentCreate: (estimateId: string, body: string) =>
    call<any>(`/pricing/estimates/${estimateId}/comments`, { method: "POST", body: JSON.stringify({ body }) }),
  commentDelete: (estimateId: string, commentId: string) =>
    call<any>(`/pricing/estimates/${estimateId}/comments/${commentId}`, { method: "DELETE" }),
  searchUsers:   (q: string) => call<any>(`/users/search?q=${encodeURIComponent(q)}`),
  regions:       () => call<any>("/manage/regions"),
  rgmsList:      () => call<any>("/manage/rgms"),
  rgmAdd:        (body: { region: string; rgm_email: string; rgm_name?: string }) =>
    call<any>("/manage/rgms", { method: "POST", body: JSON.stringify(body) }),
  rgmUpdate:     (id: number, body: { rgm_email?: string; rgm_name?: string | null; active?: boolean }) =>
    call<any>(`/manage/rgms/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  rgmDelete:     (id: number) => call<any>(`/manage/rgms/${id}`, { method: "DELETE" }),
  // Module registry (the meta-config that drives which pricing tiles are live)
  modules:           () => call<any>("/modules"),
  modulesAdmin:      () => call<any>("/manage/modules"),
  moduleUpdate:      (key: string, body: { status?: string; description?: string; approval_via_rgm?: boolean }) =>
    call<any>(`/manage/modules/${key}`, { method: "PUT", body: JSON.stringify(body) }),
  sendTestEmails:    (to?: string) => call<any>("/manage/test-emails", { method: "POST", body: JSON.stringify({ to }) }),
  // Module-scoped policy + rate card (default module='holiday' for back-compat)
  policyByModule:    (m: string) => call<any>(`/manage/policy?module=${encodeURIComponent(m)}`),
  policyByModuleUpdate: (m: string, key: string, value: string) =>
    call<any>(`/manage/policy/${key}?module=${encodeURIComponent(m)}`, { method: "PUT", body: JSON.stringify({ value }) }),
  rateCardByModule:  (m: string) => call<any>(`/manage/rate-card?module=${encodeURIComponent(m)}`),
  rateCardByModuleAdd: (m: string, body: { key: string; pence: number; effective_from: string; notes?: string }) =>
    call<any>(`/manage/rate-card?module=${encodeURIComponent(m)}`, { method: "POST", body: JSON.stringify(body) }),
  relatedByPws:  (estimateId: string) => call<any>(`/manage/estimates/${estimateId}/related-pws`),
  bulkAction: (action: "approve" | "reject" | "revert", ids: string[], reason?: string) =>
    call<any>(`/approvals/bulk/${action}`, { method: "POST", body: JSON.stringify({ ids, reason }) }),
  auditList: (params: Record<string, string> = {}) => {
    const qs = new URLSearchParams(params).toString();
    return call<any>(`/manage/audit${qs ? `?${qs}` : ""}`);
  },
  // Per-user module access (admin)
  rolesList:           () => call<any>("/manage/roles"),
  roleModulesUpdate:   (key: string, modules: string[]) =>
    call<any>(`/manage/roles/${encodeURIComponent(key)}/modules`, { method: "PUT", body: JSON.stringify({ modules }) }),
  roleGroupUpdate:     (key: string, entra_group_id: string | null, entra_group_name?: string | null) =>
    call<any>(`/manage/roles/${encodeURIComponent(key)}/group`, { method: "PUT", body: JSON.stringify({ entra_group_id, entra_group_name }) }),
  searchGroups:        (q: string) => call<any>(`/manage/groups/search?q=${encodeURIComponent(q)}`),
  syncUsersFromGroups: () => call<any>("/manage/sync/users-from-groups", { method: "POST" }),
  // Case-by-case
  calculateCase:   (body: any) => call<any>("/pricing/case/calculate", { method: "POST", body: JSON.stringify(body) }),
  createCase:      (body: any) => call<any>("/pricing/case",           { method: "POST", body: JSON.stringify(body) }),
  getCase:         (id: string) => call<any>(`/pricing/case/${id}`),
  updateCase:      (id: string, body: any) => call<any>(`/pricing/case/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteCase:      (id: string) => call<any>(`/pricing/case/${id}`, { method: "DELETE" }),
  // Annual Uplifts
  upliftsList:     () => call<any>("/pricing/uplifts"),
  upliftUpdate:    (funderId: number, body: any) => call<any>(`/pricing/uplifts/${funderId}`, { method: "PUT", body: JSON.stringify(body) }),
  // AWF
  awfList:         () => call<any>("/pricing/awf"),
  awfCreate:       (body: any) => call<any>("/pricing/awf", { method: "POST", body: JSON.stringify(body) }),
  awfUpdate:       (id: number, body: any) => call<any>(`/pricing/awf/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  awfDelete:       (id: number) => call<any>(`/pricing/awf/${id}`, { method: "DELETE" }),
  usersAccessList:     () => call<any>("/manage/users"),
  userAccessUpsert:    (oid: string, body: { user_email: string; user_display_name?: string | null; role_key?: string | null; modules: string[] }) =>
    call<any>(`/manage/users/${encodeURIComponent(oid)}`, { method: "PUT", body: JSON.stringify(body) }),
  userAccessDelete:    (oid: string) =>
    call<any>(`/manage/users/${encodeURIComponent(oid)}`, { method: "DELETE" }),
  /** Triggers a CSV download in the browser. */
  async downloadAllEstimatesCsv() {
    const token = await getToken();
    const r = await fetch(`${API_BASE}/manage/export/all-estimates`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error(`Download failed: ${r.status}`);
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = r.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] ?? "estimates.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
  },
};
